import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import KinderwerkLayout from '@/components/KinderwerkLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Baby,
  BookOpen,
  Boxes,
  Calendar,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Heart,
  Layers,
  ListChecks,
  MessageSquare,
  MoreVertical,
  Pencil,
  Plus,
  Puzzle,
  Repeat,
  Send,
  Star,
  Trash2,
  UserCog,
  Users,
  X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WERKVORM_TYPES = [
  { value: 'knutselen', label: 'Knutselen' },
  { value: 'spel', label: 'Spel' },
  { value: 'muziek', label: 'Muziek' },
  { value: 'toneel', label: 'Toneel' },
  { value: 'gesprek', label: 'Gesprek' },
];

const BINNEN_BUITEN_OPTIES = [
  { value: 'binnen', label: 'Binnen' },
  { value: 'buiten', label: 'Buiten' },
  { value: 'beide', label: 'Binnen of buiten' },
];

const MOEILIJKHEID_OPTIES = [
  { value: 'makkelijk', label: 'Makkelijk' },
  { value: 'gemiddeld', label: 'Gemiddeld' },
  { value: 'lastig', label: 'Lastig' },
];

const EMPTY_GROEP_FORM = {
  naam: '', leeftijd_min: '', leeftijd_max: '', responsible_user_id: '', member_ids: [], gevoelige_notities: '',
};
const EMPTY_SERIE_FORM = { naam: '', leeftijdsgroep_id: '', hoofdthema: '', duur_weken: '', notities: '' };
const EMPTY_LES_FORM = {
  serie_id: '', volgorde: '0', titel: '', datum: '', start_time: '', duur_minuten: '', hoofdverantwoordelijke_id: '', vrijwilliger_ids: [],
  lesdoel: '', kernboodschap: '', bijbelgedeelte: '', bijbeltekst: '', welkomstactiviteit: '', aanbidding_liederen: '',
  bijbelverhaal: '', gespreksvragen: '', spel_beweegactiviteit: '', kinderwerkje_id: '', gebed_afsluiting: '', thuisopdracht: '',
  materialen: [], materiaal_links: [], voorbereidingstijd_minuten: '', voorbereidingsinstructies: '',
};
const EMPTY_DIENST_FORM = {
  title: 'Kinderwerkdienst', slot_date: '', start_time: '10:00', end_time: '11:00', role: '',
  groep_id: '', user_id: '', user_ids: [],
};
const WEEKDAGEN = [
  { value: 0, label: 'Maandag' }, { value: 1, label: 'Dinsdag' }, { value: 2, label: 'Woensdag' },
  { value: 3, label: 'Donderdag' }, { value: 4, label: 'Vrijdag' }, { value: 5, label: 'Zaterdag' }, { value: 6, label: 'Zondag' },
];
const EMPTY_WERKVORM_FORM = {
  naam: '', leeftijd_min: '', leeftijd_max: '', duur_minuten: '', thema: '', bijbelverhaal: '', type: '',
  binnen_buiten: '', moeilijkheid: '', groepsgrootte: '', materialen: [], voorbereiding: '', uitvoering: '', bijlage_links: [],
};

const MaterialenEditor = ({ items, onChange, inventoryItems }) => {
  const update = (index, patch) => onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  const remove = (index) => onChange(items.filter((_, i) => i !== index));
  const add = () => onChange([...items, { naam: '', inventory_item_id: '' }]);
  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex gap-2 items-center">
          <Input
            placeholder="Materiaal (bijv. gekleurd papier)"
            value={item.naam}
            onChange={(e) => update(index, { naam: e.target.value })}
            className="rounded-xl flex-1"
          />
          <Select value={item.inventory_item_id || 'none'} onValueChange={(v) => update(index, { inventory_item_id: v === 'none' ? '' : v })}>
            <SelectTrigger className="rounded-xl w-56"><SelectValue placeholder="Koppel voorraadartikel" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Geen koppeling</SelectItem>
              {inventoryItems.map((inv) => <SelectItem key={inv.id} value={inv.id}>{inv.name} ({inv.current_stock} {inv.unit})</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="button" variant="ghost" size="sm" onClick={() => remove(index)} className="text-red-600 shrink-0"><X className="w-4 h-4" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="rounded-lg">
        <Plus className="w-4 h-4 mr-2" />Materiaal toevoegen
      </Button>
    </div>
  );
};

const LinksEditor = ({ items, onChange, label }) => {
  const update = (index, patch) => onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  const remove = (index) => onChange(items.filter((_, i) => i !== index));
  const add = () => onChange([...items, { titel: '', url: '' }]);
  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex gap-2 items-center">
          <Input placeholder="Titel" value={item.titel} onChange={(e) => update(index, { titel: e.target.value })} className="rounded-xl w-40" />
          <Input placeholder="https://..." value={item.url} onChange={(e) => update(index, { url: e.target.value })} className="rounded-xl flex-1" />
          <Button type="button" variant="ghost" size="sm" onClick={() => remove(index)} className="text-red-600 shrink-0"><X className="w-4 h-4" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="rounded-lg">
        <Plus className="w-4 h-4 mr-2" />{label || 'Link toevoegen'}
      </Button>
    </div>
  );
};

const KinderwerkPage = () => {
  const { hasCapability } = useAuth();
  const canManage = hasCapability('kinderwerk_manage');

  const [activeTab, setActiveTab] = useState('groepen');
  const [dashboard, setDashboard] = useState(null);
  const [groepen, setGroepen] = useState([]);
  const [series, setSeries] = useState([]);
  const [werkvormen, setWerkvormen] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [members, setMembers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [werkvormSearch, setWerkvormSearch] = useState('');
  const [werkvormTypeFilter, setWerkvormTypeFilter] = useState('all');

  const [groepDialogOpen, setGroepDialogOpen] = useState(false);
  const [editingGroep, setEditingGroep] = useState(null);
  const [groepForm, setGroepForm] = useState(EMPTY_GROEP_FORM);
  const [newChildFormOpen, setNewChildFormOpen] = useState(false);
  const [newChildName, setNewChildName] = useState('');
  const [newChildEmail, setNewChildEmail] = useState('');
  const [newChildBirthdate, setNewChildBirthdate] = useState('');
  const [newChildParentId, setNewChildParentId] = useState('');

  const [serieDialogOpen, setSerieDialogOpen] = useState(false);
  const [editingSerie, setEditingSerie] = useState(null);
  const [serieForm, setSerieForm] = useState(EMPTY_SERIE_FORM);

  const [lessenSerie, setLessenSerie] = useState(null);
  const [lessen, setLessen] = useState([]);

  const [lesDialogOpen, setLesDialogOpen] = useState(false);
  const [editingLes, setEditingLes] = useState(null);
  const [lesForm, setLesForm] = useState(EMPTY_LES_FORM);

  const [werkvormDialogOpen, setWerkvormDialogOpen] = useState(false);
  const [editingWerkvorm, setEditingWerkvorm] = useState(null);
  const [werkvormForm, setWerkvormForm] = useState(EMPTY_WERKVORM_FORM);

  const [notitiesWerkvorm, setNotitiesWerkvorm] = useState(null);
  const [notities, setNotities] = useState([]);
  const [notitieText, setNotitieText] = useState('');
  const [notitieRating, setNotitieRating] = useState('');

  const [kinderwerkTeam, setKinderwerkTeam] = useState([]);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [teamDialogSource, setTeamDialogSource] = useState('team');
  const [selectedTeamUserId, setSelectedTeamUserId] = useState('');
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState('');
  const [teamInviteRole, setTeamInviteRole] = useState('kinderwerkvrijwilliger');
  const [teamInviteLink, setTeamInviteLink] = useState(null);

  const [lesSuggestions, setLesSuggestions] = useState([]);
  const [scheduleSerie, setScheduleSerie] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ weekday: 6, start_time: '10:00', start_date: '', skip_dates: '' });
  const [scheduleResult, setScheduleResult] = useState(null);
  const [scheduling, setScheduling] = useState(false);

  const [rosterYear, setRosterYear] = useState(() => new Date().getFullYear());
  const [rosterMonth, setRosterMonth] = useState(() => new Date().getMonth() + 1);
  const [rosterSummary, setRosterSummary] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const [dienstDialogOpen, setDienstDialogOpen] = useState(false);
  const [editingDienst, setEditingDienst] = useState(null);
  const [dienstForm, setDienstForm] = useState(EMPTY_DIENST_FORM);
  const [dienstSuggestions, setDienstSuggestions] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  const fetchRosterSummary = async () => {
    try {
      const res = await axios.get(`${API}/kinderwerk/roster/summary`, { params: { year: rosterYear, month: rosterMonth } });
      setRosterSummary(res.data);
    } catch (error) {
      toast.error('Kon rooster niet laden');
    }
  };

  useEffect(() => {
    if (!lesDialogOpen || !editingLes?.id) { setLesSuggestions([]); return; }
    let cancelled = false;
    axios.get(`${API}/kinderwerk/lessen/${editingLes.id}/schedule/suggestions`)
      .then((res) => { if (!cancelled) setLesSuggestions(res.data); })
      .catch(() => { if (!cancelled) setLesSuggestions([]); });
    return () => { cancelled = true; };
  }, [lesDialogOpen, editingLes]);

  useEffect(() => {
    if (activeTab !== 'rooster') return;
    fetchRosterSummary();
  }, [activeTab, rosterYear, rosterMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!dienstDialogOpen || !editingDienst?.id) { setDienstSuggestions([]); return; }
    let cancelled = false;
    axios.get(`${API}/kinderwerk/diensten/${editingDienst.id}/schedule/suggestions`)
      .then((res) => { if (!cancelled) setDienstSuggestions(res.data); })
      .catch(() => { if (!cancelled) setDienstSuggestions([]); });
    return () => { cancelled = true; };
  }, [dienstDialogOpen, editingDienst]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dashRes, groepenRes, seriesRes, werkvormenRes, teamRes, membersRes, invRes, kwTeamRes] = await Promise.all([
        axios.get(`${API}/kinderwerk/dashboard`),
        axios.get(`${API}/kinderwerk/groepen`),
        axios.get(`${API}/kinderwerk/series`),
        axios.get(`${API}/kinderwerk/werkvormen`),
        axios.get(`${API}/team`),
        axios.get(`${API}/members`, { params: { include_children: true } }),
        axios.get(`${API}/inventory/items`).catch(() => ({ data: [] })),
        axios.get(`${API}/kinderwerk/team`),
      ]);
      setDashboard(dashRes.data);
      setGroepen(groepenRes.data);
      setSeries(seriesRes.data);
      setWerkvormen(werkvormenRes.data);
      setTeamMembers(teamRes.data);
      setMembers(membersRes.data);
      setInventoryItems(invRes.data);
      setKinderwerkTeam(kwTeamRes.data);
    } catch (error) {
      toast.error('Kon kinderwerk niet laden');
    } finally {
      setLoading(false);
    }
  };

  const refreshDashboardAndSeries = async () => {
    const [dashRes, seriesRes] = await Promise.all([
      axios.get(`${API}/kinderwerk/dashboard`),
      axios.get(`${API}/kinderwerk/series`),
    ]);
    setDashboard(dashRes.data);
    setSeries(seriesRes.data);
  };

  const teamMemberName = (id) => teamMembers.find((t) => t.id === id)?.name || '-';

  // ---------- Groepen ----------
  const openCreateGroep = () => { setEditingGroep(null); setGroepForm(EMPTY_GROEP_FORM); setNewChildFormOpen(false); setGroepDialogOpen(true); };
  const openEditGroep = (groep) => {
    setEditingGroep(groep);
    setGroepForm({
      naam: groep.naam || '',
      leeftijd_min: groep.leeftijd_min != null ? String(groep.leeftijd_min) : '',
      leeftijd_max: groep.leeftijd_max != null ? String(groep.leeftijd_max) : '',
      responsible_user_id: groep.responsible_user_id || '',
      member_ids: groep.member_ids || [],
      gevoelige_notities: groep.gevoelige_notities || '',
    });
    setNewChildFormOpen(false);
    setGroepDialogOpen(true);
  };
  const submitNewChild = async (event) => {
    event.preventDefault();
    if (!newChildName.trim()) { toast.error('Naam is verplicht'); return; }
    try {
      let response = await axios.post(`${API}/members`, {
        name: newChildName.trim(),
        email: newChildEmail.trim() || null,
        date_of_birth: newChildBirthdate || null,
      });
      if (newChildParentId) {
        response = await axios.post(`${API}/members/${response.data.id}/household`, { parent_member_id: newChildParentId });
      }
      setMembers((current) => [...current, response.data]);
      setGroepForm((current) => ({ ...current, member_ids: [...current.member_ids, response.data.id] }));
      setNewChildName('');
      setNewChildEmail('');
      setNewChildBirthdate('');
      setNewChildParentId('');
      setNewChildFormOpen(false);
      toast.success('Kind toegevoegd aan het ledenbestand en deze groep');
    } catch (error) {
      toast.error('Kon kind niet toevoegen');
    }
  };
  const submitGroep = async (event) => {
    event.preventDefault();
    if (!groepForm.naam) { toast.error('Naam is verplicht'); return; }
    const payload = {
      ...groepForm,
      leeftijd_min: groepForm.leeftijd_min ? parseInt(groepForm.leeftijd_min, 10) : null,
      leeftijd_max: groepForm.leeftijd_max ? parseInt(groepForm.leeftijd_max, 10) : null,
      responsible_user_id: groepForm.responsible_user_id || null,
    };
    try {
      if (editingGroep) {
        await axios.put(`${API}/kinderwerk/groepen/${editingGroep.id}`, payload);
        toast.success('Groep bijgewerkt');
      } else {
        await axios.post(`${API}/kinderwerk/groepen`, payload);
        toast.success('Groep toegevoegd');
      }
      setGroepDialogOpen(false);
      const res = await axios.get(`${API}/kinderwerk/groepen`);
      setGroepen(res.data);
    } catch (error) {
      toast.error('Opslaan mislukt');
    }
  };
  const deleteGroep = async (groep) => {
    if (!window.confirm(`Groep "${groep.naam}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/kinderwerk/groepen/${groep.id}`);
      setGroepen((current) => current.filter((g) => g.id !== groep.id));
      toast.success('Groep verwijderd');
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  // ---------- Series ----------
  const openCreateSerie = () => { setEditingSerie(null); setSerieForm(EMPTY_SERIE_FORM); setSerieDialogOpen(true); };
  const openEditSerie = (serie) => {
    setEditingSerie(serie);
    setSerieForm({
      naam: serie.naam || '',
      leeftijdsgroep_id: serie.leeftijdsgroep_id || '',
      hoofdthema: serie.hoofdthema || '',
      duur_weken: serie.duur_weken != null ? String(serie.duur_weken) : '',
      notities: serie.notities || '',
    });
    setSerieDialogOpen(true);
  };
  const submitSerie = async (event) => {
    event.preventDefault();
    if (!serieForm.naam) { toast.error('Naam is verplicht'); return; }
    const payload = {
      ...serieForm,
      leeftijdsgroep_id: serieForm.leeftijdsgroep_id || null,
      duur_weken: serieForm.duur_weken ? parseInt(serieForm.duur_weken, 10) : null,
    };
    try {
      if (editingSerie) {
        await axios.put(`${API}/kinderwerk/series/${editingSerie.id}`, payload);
        toast.success('Serie bijgewerkt');
      } else {
        await axios.post(`${API}/kinderwerk/series`, payload);
        toast.success('Serie toegevoegd');
      }
      setSerieDialogOpen(false);
      const res = await axios.get(`${API}/kinderwerk/series`);
      setSeries(res.data);
    } catch (error) {
      toast.error('Opslaan mislukt');
    }
  };
  const deleteSerie = async (serie) => {
    if (!window.confirm(`Serie "${serie.naam}" en al zijn lessen verwijderen?`)) return;
    try {
      await axios.delete(`${API}/kinderwerk/series/${serie.id}`);
      setSeries((current) => current.filter((s) => s.id !== serie.id));
      toast.success('Serie verwijderd');
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  // ---------- Lessen ----------
  const openLessenDialog = async (serie) => {
    setLessenSerie(serie);
    try {
      const res = await axios.get(`${API}/kinderwerk/lessen`, { params: { serie_id: serie.id } });
      setLessen(res.data);
    } catch (error) {
      toast.error('Kon lessen niet laden');
    }
  };
  const openCreateLes = () => {
    setEditingLes(null);
    setLesForm({ ...EMPTY_LES_FORM, serie_id: lessenSerie.id, volgorde: String(lessen.length) });
    setLesDialogOpen(true);
  };
  const openEditLes = (les) => {
    setEditingLes(les);
    setLesForm({
      serie_id: les.serie_id,
      volgorde: String(les.volgorde ?? 0),
      titel: les.titel || '',
      datum: les.datum || '',
      start_time: les.start_time || '',
      duur_minuten: les.duur_minuten != null ? String(les.duur_minuten) : '',
      hoofdverantwoordelijke_id: les.hoofdverantwoordelijke_id || '',
      vrijwilliger_ids: les.vrijwilliger_ids || [],
      lesdoel: les.lesdoel || '',
      kernboodschap: les.kernboodschap || '',
      bijbelgedeelte: les.bijbelgedeelte || '',
      bijbeltekst: les.bijbeltekst || '',
      welkomstactiviteit: les.welkomstactiviteit || '',
      aanbidding_liederen: les.aanbidding_liederen || '',
      bijbelverhaal: les.bijbelverhaal || '',
      gespreksvragen: les.gespreksvragen || '',
      spel_beweegactiviteit: les.spel_beweegactiviteit || '',
      kinderwerkje_id: les.kinderwerkje_id || '',
      gebed_afsluiting: les.gebed_afsluiting || '',
      thuisopdracht: les.thuisopdracht || '',
      materialen: les.materialen || [],
      materiaal_links: les.materiaal_links || [],
      voorbereidingstijd_minuten: les.voorbereidingstijd_minuten != null ? String(les.voorbereidingstijd_minuten) : '',
      voorbereidingsinstructies: les.voorbereidingsinstructies || '',
    });
    setLesDialogOpen(true);
  };
  const submitLes = async (event) => {
    event.preventDefault();
    if (!lesForm.titel) { toast.error('Titel is verplicht'); return; }
    const payload = {
      ...lesForm,
      volgorde: parseInt(lesForm.volgorde, 10) || 0,
      datum: lesForm.datum || null,
      start_time: lesForm.start_time || null,
      duur_minuten: lesForm.duur_minuten ? parseInt(lesForm.duur_minuten, 10) : null,
      voorbereidingstijd_minuten: lesForm.voorbereidingstijd_minuten ? parseInt(lesForm.voorbereidingstijd_minuten, 10) : null,
      hoofdverantwoordelijke_id: lesForm.hoofdverantwoordelijke_id || null,
      kinderwerkje_id: lesForm.kinderwerkje_id || null,
      materialen: lesForm.materialen.map(({ naam, inventory_item_id }) => ({ naam, inventory_item_id: inventory_item_id || null })),
    };
    try {
      if (editingLes) {
        await axios.put(`${API}/kinderwerk/lessen/${editingLes.id}`, payload);
        toast.success('Les bijgewerkt');
      } else {
        await axios.post(`${API}/kinderwerk/lessen`, payload);
        toast.success('Les toegevoegd');
      }
      setLesDialogOpen(false);
      const res = await axios.get(`${API}/kinderwerk/lessen`, { params: { serie_id: lessenSerie.id } });
      setLessen(res.data);
      refreshDashboardAndSeries();
    } catch (error) {
      toast.error('Opslaan mislukt');
    }
  };
  const deleteLes = async (les) => {
    if (!window.confirm(`Les "${les.titel}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/kinderwerk/lessen/${les.id}`);
      setLessen((current) => current.filter((l) => l.id !== les.id));
      refreshDashboardAndSeries();
      toast.success('Les verwijderd');
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  // ---------- Werkvormen ----------
  const fetchWerkvormen = async () => {
    const res = await axios.get(`${API}/kinderwerk/werkvormen`, {
      params: {
        q: werkvormSearch || undefined,
        werkvorm_type: werkvormTypeFilter !== 'all' ? werkvormTypeFilter : undefined,
      },
    });
    setWerkvormen(res.data);
  };
  useEffect(() => { if (!loading) fetchWerkvormen(); }, [werkvormSearch, werkvormTypeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreateWerkvorm = () => { setEditingWerkvorm(null); setWerkvormForm(EMPTY_WERKVORM_FORM); setWerkvormDialogOpen(true); };
  const openEditWerkvorm = (werkvorm) => {
    setEditingWerkvorm(werkvorm);
    setWerkvormForm({
      naam: werkvorm.naam || '',
      leeftijd_min: werkvorm.leeftijd_min != null ? String(werkvorm.leeftijd_min) : '',
      leeftijd_max: werkvorm.leeftijd_max != null ? String(werkvorm.leeftijd_max) : '',
      duur_minuten: werkvorm.duur_minuten != null ? String(werkvorm.duur_minuten) : '',
      thema: werkvorm.thema || '',
      bijbelverhaal: werkvorm.bijbelverhaal || '',
      type: werkvorm.type || '',
      binnen_buiten: werkvorm.binnen_buiten || '',
      moeilijkheid: werkvorm.moeilijkheid || '',
      groepsgrootte: werkvorm.groepsgrootte || '',
      materialen: werkvorm.materialen || [],
      voorbereiding: werkvorm.voorbereiding || '',
      uitvoering: werkvorm.uitvoering || '',
      bijlage_links: werkvorm.bijlage_links || [],
    });
    setWerkvormDialogOpen(true);
  };
  const submitWerkvorm = async (event) => {
    event.preventDefault();
    if (!werkvormForm.naam) { toast.error('Naam is verplicht'); return; }
    const payload = {
      ...werkvormForm,
      leeftijd_min: werkvormForm.leeftijd_min ? parseInt(werkvormForm.leeftijd_min, 10) : null,
      leeftijd_max: werkvormForm.leeftijd_max ? parseInt(werkvormForm.leeftijd_max, 10) : null,
      duur_minuten: werkvormForm.duur_minuten ? parseInt(werkvormForm.duur_minuten, 10) : null,
      materialen: werkvormForm.materialen.map(({ naam, inventory_item_id }) => ({ naam, inventory_item_id: inventory_item_id || null })),
    };
    try {
      if (editingWerkvorm) {
        await axios.put(`${API}/kinderwerk/werkvormen/${editingWerkvorm.id}`, payload);
        toast.success('Werkvorm bijgewerkt');
      } else {
        await axios.post(`${API}/kinderwerk/werkvormen`, payload);
        toast.success('Werkvorm toegevoegd');
      }
      setWerkvormDialogOpen(false);
      fetchWerkvormen();
    } catch (error) {
      toast.error('Opslaan mislukt');
    }
  };
  const deleteWerkvorm = async (werkvorm) => {
    if (!window.confirm(`Werkvorm "${werkvorm.naam}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/kinderwerk/werkvormen/${werkvorm.id}`);
      setWerkvormen((current) => current.filter((w) => w.id !== werkvorm.id));
      toast.success('Werkvorm verwijderd');
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };
  const toggleFavoriet = async (werkvorm) => {
    try {
      if (werkvorm.favorited) {
        await axios.delete(`${API}/kinderwerk/werkvormen/${werkvorm.id}/favoriet`);
      } else {
        await axios.post(`${API}/kinderwerk/werkvormen/${werkvorm.id}/favoriet`);
      }
      fetchWerkvormen();
    } catch (error) {
      toast.error('Kon favoriet niet bijwerken');
    }
  };

  const openNotities = async (werkvorm) => {
    setNotitiesWerkvorm(werkvorm);
    setNotitieText('');
    setNotitieRating('');
    try {
      const res = await axios.get(`${API}/kinderwerk/werkvormen/${werkvorm.id}/notities`);
      setNotities(res.data);
    } catch (error) {
      toast.error('Kon notities niet laden');
    }
  };
  const submitNotitie = async (event) => {
    event.preventDefault();
    if (!notitieText.trim()) return;
    try {
      await axios.post(`${API}/kinderwerk/werkvormen/${notitiesWerkvorm.id}/notities`, {
        notitie: notitieText.trim(),
        beoordeling: notitieRating ? parseInt(notitieRating, 10) : null,
      });
      setNotitieText('');
      setNotitieRating('');
      const res = await axios.get(`${API}/kinderwerk/werkvormen/${notitiesWerkvorm.id}/notities`);
      setNotities(res.data);
    } catch (error) {
      toast.error('Kon notitie niet toevoegen');
    }
  };

  // ---------- Kinderwerkteam ----------
  const availableTeamUsers = teamMembers.filter((u) => !kinderwerkTeam.some((k) => k.id === u.id));
  const availableTeamMembers = members.filter((m) => !kinderwerkTeam.some((k) => k.source_member_id === m.id));

  const openTeamDialog = () => {
    setTeamDialogSource('team');
    setSelectedTeamUserId('');
    setSelectedTeamMemberId('');
    setTeamInviteRole('kinderwerkvrijwilliger');
    setTeamInviteLink(null);
    setTeamDialogOpen(true);
  };
  const refreshKinderwerkTeam = async () => {
    const res = await axios.get(`${API}/kinderwerk/team`);
    setKinderwerkTeam(res.data);
  };
  const addExistingTeamMember = async (event) => {
    event.preventDefault();
    if (!selectedTeamUserId) { toast.error('Kies een teamlid'); return; }
    try {
      await axios.post(`${API}/kinderwerk/team/members`, { user_id: selectedTeamUserId });
      toast.success('Teamlid toegevoegd aan het kinderwerkteam');
      setTeamDialogOpen(false);
      refreshKinderwerkTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Toevoegen mislukt');
    }
  };
  const inviteTeamMember = async (event) => {
    event.preventDefault();
    if (!selectedTeamMemberId) { toast.error('Kies een gemeentelid'); return; }
    try {
      const response = await axios.post(`${API}/kinderwerk/team/members/from-member`, {
        member_id: selectedTeamMemberId,
        roles: [teamInviteRole],
      });
      toast.success('Uitnodiging verstuurd');
      setTeamInviteLink(response.data.invite_link);
      refreshKinderwerkTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Uitnodigen mislukt');
    }
  };
  const removeKinderwerkTeamMember = async (membershipId) => {
    if (!window.confirm('Dit teamlid uit het kinderwerkteam verwijderen?')) return;
    try {
      await axios.delete(`${API}/kinderwerk/team/members/${membershipId}`);
      toast.success('Teamlid verwijderd');
      refreshKinderwerkTeam();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  // ---------- Serie automatisch inplannen ----------
  const openScheduleDialog = (serie) => {
    setScheduleSerie(serie);
    setScheduleForm({ weekday: 6, start_time: '10:00', start_date: '', skip_dates: '' });
    setScheduleResult(null);
  };
  const submitSchedule = async (event) => {
    event.preventDefault();
    if (!scheduleForm.start_date) { toast.error('Kies een startdatum'); return; }
    setScheduling(true);
    try {
      const skipDates = scheduleForm.skip_dates.split(',').map((d) => d.trim()).filter(Boolean);
      const response = await axios.post(`${API}/kinderwerk/series/${scheduleSerie.id}/schedule`, {
        weekday: Number(scheduleForm.weekday),
        start_time: scheduleForm.start_time,
        start_date: scheduleForm.start_date,
        skip_dates: skipDates,
      });
      setScheduleResult(response.data);
      if (response.data.scheduled.length) {
        toast.success(`${response.data.scheduled.length} les(sen) van een datum voorzien`);
      } else {
        toast.warning('Geen lessen ingepland — alle lessen hadden al een datum');
      }
      refreshDashboardAndSeries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Inplannen mislukt');
    } finally {
      setScheduling(false);
    }
  };

  // ---------- Maandrooster ----------
  const shiftRosterMonth = (delta) => {
    const total = rosterYear * 12 + (rosterMonth - 1) + delta;
    setRosterYear(Math.floor(total / 12));
    setRosterMonth((total % 12) + 1);
  };
  const rosterMonthLabel = new Intl.DateTimeFormat('nl-NL', { month: 'long', year: 'numeric' }).format(new Date(rosterYear, rosterMonth - 1, 1));
  const publishRoster = async () => {
    if (!window.confirm(`Kinderwerkrooster voor ${rosterMonthLabel} versturen? Elke betrokken kinderleider ontvangt een e-mail met hun planning deze maand.`)) return;
    setPublishing(true);
    try {
      const response = await axios.post(`${API}/kinderwerk/roster/${rosterYear}/${rosterMonth}/publish`);
      toast.success(`${response.data.recipients_notified} kinderleider(s) genotificeerd`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Publiceren mislukt');
    } finally {
      setPublishing(false);
    }
  };

  // ---------- Kinderwerkdiensten (los van lesinhoud) ----------
  const openCreateDienst = () => {
    setEditingDienst(null);
    setDienstForm(EMPTY_DIENST_FORM);
    setDienstDialogOpen(true);
  };
  const openEditDienst = (dienst) => {
    setEditingDienst(dienst);
    setDienstForm({
      title: dienst.title, slot_date: dienst.slot_date, start_time: dienst.start_time, end_time: dienst.end_time,
      role: dienst.role || '', groep_id: dienst.groep_id || '', user_id: dienst.user_id || '', user_ids: [],
    });
    setDienstDialogOpen(true);
  };
  const submitDienst = async (event) => {
    event.preventDefault();
    if (!dienstForm.title || !dienstForm.slot_date || !dienstForm.start_time || !dienstForm.end_time) {
      toast.error('Titel, datum, start- en eindtijd zijn verplicht');
      return;
    }
    const basePayload = {
      module: 'kinderwerk',
      title: dienstForm.title,
      slot_date: dienstForm.slot_date,
      start_time: dienstForm.start_time,
      end_time: dienstForm.end_time,
      role: dienstForm.role || null,
      groep_id: dienstForm.groep_id || null,
    };
    try {
      if (editingDienst) {
        const selectedPerson = kinderwerkTeam.find((m) => m.id === dienstForm.user_id);
        await axios.put(`${API}/team-slots/${editingDienst.id}`, {
          ...basePayload, user_id: dienstForm.user_id || null, name: selectedPerson ? selectedPerson.name : null,
        });
        toast.success('Dienst bijgewerkt');
      } else if (dienstForm.user_ids.length > 0) {
        // Eén dienst per gekozen kinderleider -- vaak heb je per groep
        // meerdere leiders nodig, dus meerdere in één keer inplannen.
        await Promise.all(dienstForm.user_ids.map((userId) => {
          const person = kinderwerkTeam.find((m) => m.id === userId);
          return axios.post(`${API}/team-slots`, { ...basePayload, user_id: userId, name: person?.name || null });
        }));
        toast.success(`${dienstForm.user_ids.length} kinderleider(s) ingepland`);
      } else {
        await axios.post(`${API}/team-slots`, { ...basePayload, user_id: null, name: null });
        toast.success('Dienst ingepland (nog niemand toegewezen)');
      }
      setDienstDialogOpen(false);
      fetchRosterSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Opslaan mislukt');
    }
  };
  const deleteDienst = async (dienst) => {
    if (!window.confirm(`Dienst "${dienst.title}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/team-slots/${dienst.id}`);
      toast.success('Dienst verwijderd');
      fetchRosterSummary();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  if (loading) {
    return (
      <KinderwerkLayout>
        <div className="p-6 text-center text-slate-400">Kinderwerk laden...</div>
      </KinderwerkLayout>
    );
  }

  return (
    <KinderwerkLayout>
      <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Baby className="w-6 h-6 text-teal-600" />Kinderwerk</h1>
          <p className="text-slate-500 text-sm mt-1">Groepen, lessenseries en de werkvormenbibliotheek.</p>
        </div>

        {dashboard && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Groepen</p><p className="text-2xl font-bold text-slate-900">{dashboard.groepen}</p></CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Series</p><p className="text-2xl font-bold text-slate-900">{dashboard.series}</p></CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Lessen deze week</p><p className="text-2xl font-bold text-slate-900">{dashboard.lessen_deze_week}</p></CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Werkvormen</p><p className="text-2xl font-bold text-slate-900">{dashboard.werkvormen}</p></CardContent></Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full max-w-full justify-start overflow-x-auto">
            <TabsTrigger value="groepen"><Users className="w-4 h-4 mr-2" />Groepen</TabsTrigger>
            <TabsTrigger value="series"><Layers className="w-4 h-4 mr-2" />Lessenseries</TabsTrigger>
            <TabsTrigger value="werkvormen"><Puzzle className="w-4 h-4 mr-2" />Werkvormenbibliotheek</TabsTrigger>
            <TabsTrigger value="team"><UserCog className="w-4 h-4 mr-2" />Kinderwerkteam</TabsTrigger>
            <TabsTrigger value="rooster"><CalendarCheck className="w-4 h-4 mr-2" />Rooster</TabsTrigger>
          </TabsList>

          {/* GROEPEN */}
          <TabsContent value="groepen" className="space-y-4">
            {canManage && (
              <div className="flex justify-end">
                <Button onClick={openCreateGroep} className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />Nieuwe groep
                </Button>
              </div>
            )}
            {groepen.length === 0 ? (
              <Card className="border-0 shadow-sm"><CardContent className="p-10 text-center text-slate-500">
                <Baby className="w-10 h-10 text-slate-300 mx-auto mb-3" />Nog geen groepen aangemaakt.
              </CardContent></Card>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {groepen.map((groep) => (
                  <Card key={groep.id} className="border-0 shadow-sm">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-slate-900">{groep.naam}</h3>
                          {(groep.leeftijd_min || groep.leeftijd_max) && (
                            <p className="text-sm text-slate-500">{groep.leeftijd_min ?? '?'}–{groep.leeftijd_max ?? '?'} jaar</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">Eindverantwoordelijke: {teamMemberName(groep.responsible_user_id)}</p>
                          <p className="text-xs text-slate-400">{(groep.member_ids || []).length} kinderen in deze groep</p>
                        </div>
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditGroep(groep)}><Pencil className="w-4 h-4 mr-2" />Bewerken</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => deleteGroep(groep)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Verwijderen</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      {'gevoelige_notities' in groep && groep.gevoelige_notities && (
                        <div className="mt-3 text-xs bg-amber-50 text-amber-800 rounded-lg p-2">
                          <span className="font-semibold">Bijzonderheden: </span>{groep.gevoelige_notities}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SERIES */}
          <TabsContent value="series" className="space-y-4">
            {canManage && (
              <div className="flex justify-end">
                <Button onClick={openCreateSerie} className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />Nieuwe serie
                </Button>
              </div>
            )}
            {series.length === 0 ? (
              <Card className="border-0 shadow-sm"><CardContent className="p-10 text-center text-slate-500">
                <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />Nog geen lessenseries aangemaakt.
              </CardContent></Card>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {series.map((serie) => (
                  <Card key={serie.id} className="border-0 shadow-sm">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-slate-900">{serie.naam}</h3>
                          {serie.hoofdthema && <p className="text-sm text-slate-500">{serie.hoofdthema}</p>}
                          <p className="text-xs text-slate-400 mt-1">{serie.les_count ?? 0} lessen{serie.duur_weken ? ` · ${serie.duur_weken} weken` : ''}</p>
                        </div>
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditSerie(serie)}><Pencil className="w-4 h-4 mr-2" />Bewerken</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => deleteSerie(serie)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Verwijderen</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" className="rounded-xl" onClick={() => openLessenDialog(serie)}>
                          <ListChecks className="w-4 h-4 mr-2" />Lessen beheren
                        </Button>
                        {canManage && (
                          <Button variant="outline" size="sm" className="rounded-xl" onClick={() => openScheduleDialog(serie)}>
                            <Repeat className="w-4 h-4 mr-2" />Serie automatisch inplannen
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* WERKVORMEN */}
          <TabsContent value="werkvormen" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                <Input placeholder="Zoek op naam, thema of Bijbelverhaal" value={werkvormSearch} onChange={(e) => setWerkvormSearch(e.target.value)} className="rounded-xl sm:max-w-xs" />
                <Select value={werkvormTypeFilter} onValueChange={setWerkvormTypeFilter}>
                  <SelectTrigger className="rounded-xl sm:w-48"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle types</SelectItem>
                    {WERKVORM_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {canManage && (
                <Button onClick={openCreateWerkvorm} className="rounded-xl bg-teal-600 hover:bg-teal-700 shrink-0">
                  <Plus className="w-4 h-4 mr-2" />Nieuwe werkvorm
                </Button>
              )}
            </div>
            {werkvormen.length === 0 ? (
              <Card className="border-0 shadow-sm"><CardContent className="p-10 text-center text-slate-500">
                <Puzzle className="w-10 h-10 text-slate-300 mx-auto mb-3" />Geen werkvormen gevonden.
              </CardContent></Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {werkvormen.map((werkvorm) => (
                  <Card key={werkvorm.id} className="border-0 shadow-sm">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-slate-900">{werkvorm.naam}</h3>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggleFavoriet(werkvorm)}>
                            <Star className={`w-4 h-4 ${werkvorm.favorited ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
                          </Button>
                          {canManage && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditWerkvorm(werkvorm)}><Pencil className="w-4 h-4 mr-2" />Bewerken</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => deleteWerkvorm(werkvorm)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Verwijderen</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {werkvorm.thema && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{werkvorm.thema}</span>}
                        {werkvorm.type && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">{WERKVORM_TYPES.find((t) => t.value === werkvorm.type)?.label || werkvorm.type}</span>}
                        {(werkvorm.leeftijd_min || werkvorm.leeftijd_max) && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{werkvorm.leeftijd_min ?? '?'}–{werkvorm.leeftijd_max ?? '?'} jr</span>}
                        {werkvorm.duur_minuten && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{werkvorm.duur_minuten} min</span>}
                      </div>
                      {werkvorm.materialen?.length > 0 && (
                        <div className="mt-2 text-xs text-slate-500 flex items-start gap-1.5">
                          <Boxes className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <span>{werkvorm.materialen.map((m) => `${m.naam}${m.voorraad ? ` (${m.voorraad.current_stock} ${m.voorraad.unit})` : ''}`).join(', ')}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-4">
                        <span className="text-xs text-slate-400 flex items-center gap-1"><Heart className="w-3.5 h-3.5" />{werkvorm.favorite_count || 0}</span>
                        <Button variant="outline" size="sm" className="rounded-lg" onClick={() => openNotities(werkvorm)}>
                          <MessageSquare className="w-3.5 h-3.5 mr-2" />Notities
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* KINDERWERKTEAM */}
          <TabsContent value="team" className="space-y-4">
            {canManage && (
              <div className="flex justify-end">
                <Button onClick={openTeamDialog} className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />Teamlid toevoegen
                </Button>
              </div>
            )}
            {kinderwerkTeam.length === 0 ? (
              <Card className="border-0 shadow-sm"><CardContent className="p-10 text-center text-slate-500">
                <UserCog className="w-10 h-10 text-slate-300 mx-auto mb-3" />Nog geen kinderwerkteam samengesteld.
              </CardContent></Card>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {kinderwerkTeam.map((member) => (
                  <Card key={member.id} className="border-0 shadow-sm">
                    <CardContent className="flex items-center gap-4 p-5">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-100 font-semibold text-teal-800">{member.name.charAt(0)}</div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-slate-900">{member.name}</p>
                        <p className="truncate text-sm text-slate-500">{member.email}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {(member.roles || []).map((r) => <Badge key={r} variant="secondary">{r}</Badge>)}
                          {member.source_member_id && <Badge variant="outline">Gemeentelid</Badge>}
                        </div>
                      </div>
                      {canManage && member.membership_id && (
                        <Button variant="ghost" size="icon" onClick={() => removeKinderwerkTeamMember(member.membership_id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ROOSTER */}
          <TabsContent value="rooster" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="rounded-xl" onClick={() => shiftRosterMonth(-1)}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="min-w-36 text-center text-sm font-medium capitalize text-slate-700">{rosterMonthLabel}</span>
                <Button variant="outline" size="icon" className="rounded-xl" onClick={() => shiftRosterMonth(1)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {canManage && (
                  <Button variant="outline" onClick={openCreateDienst} className="rounded-xl">
                    <Plus className="w-4 h-4 mr-2" />Snel inplannen
                  </Button>
                )}
                {canManage && (
                  <Button
                    onClick={publishRoster}
                    disabled={publishing || (!rosterSummary?.lessen?.length && !rosterSummary?.diensten?.length)}
                    className="rounded-xl bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <Send className="w-4 h-4 mr-2" />{publishing ? 'Versturen...' : 'Publiceer rooster'}
                  </Button>
                )}
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2 space-y-6">
                <div className="space-y-3">
                  <h3 className="font-semibold text-slate-900">Lessen</h3>
                  {(rosterSummary?.lessen || []).map((les) => (
                    <Card key={les.id} className="border-0 shadow-sm">
                      <CardContent className="flex items-center justify-between gap-3 p-4">
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-900">{les.titel}{les.serie_naam ? ` — ${les.serie_naam}` : ''}</p>
                          <p className="mt-1 text-sm text-slate-500">{les.datum} {les.start_time ? `· ${les.start_time}` : ''}</p>
                        </div>
                        <span className="shrink-0 text-xs text-slate-500">{(les.vrijwilliger_ids?.length || 0) + (les.hoofdverantwoordelijke_id ? 1 : 0)} ingedeeld</span>
                      </CardContent>
                    </Card>
                  ))}
                  {!rosterSummary?.lessen?.length && (
                    <Card className="border-0 shadow-sm"><CardContent className="p-10 text-center text-slate-500">
                      <CalendarCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />Geen lessen deze maand.
                    </CardContent></Card>
                  )}
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-slate-900">Diensten (los van lesinhoud)</h3>
                  {(rosterSummary?.diensten || []).map((dienst) => (
                    <Card key={dienst.id} className="border-0 shadow-sm">
                      <CardContent className="flex items-center justify-between gap-3 p-4">
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-900">{dienst.title}{dienst.role ? ` — ${dienst.role}` : ''}{dienst.groep_naam ? ` (${dienst.groep_naam})` : ''}</p>
                          <p className="mt-1 text-sm text-slate-500">
                            {dienst.slot_date} · {dienst.start_time}-{dienst.end_time} · {dienst.name || 'Nog niet toegewezen'}
                          </p>
                        </div>
                        {canManage && (
                          <div className="flex shrink-0 gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEditDienst(dienst)}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => deleteDienst(dienst)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  {!rosterSummary?.diensten?.length && <p className="text-sm text-slate-400">Nog geen losse diensten deze maand.</p>}
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-slate-900">Planningsbelasting per kinderleider</h3>
                {(rosterSummary?.members || []).map((member) => (
                  <Card key={member.user_id} className="border-0 shadow-sm">
                    <CardContent className="flex items-center justify-between gap-3 p-4">
                      <p className="truncate font-medium text-slate-900">{member.name}</p>
                      <span className="shrink-0 text-xs text-slate-500">{member.count_this_month}x deze maand</span>
                    </CardContent>
                  </Card>
                ))}
                {!rosterSummary?.members?.length && <p className="text-sm text-slate-400">Nog niemand ingedeeld deze maand.</p>}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Groep dialoog */}
        <Dialog open={groepDialogOpen} onOpenChange={setGroepDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingGroep ? 'Groep bewerken' : 'Nieuwe groep'}</DialogTitle></DialogHeader>
            <form onSubmit={submitGroep} className="space-y-4">
              <div className="space-y-2">
                <Label>Naam *</Label>
                <Input value={groepForm.naam} onChange={(e) => setGroepForm({ ...groepForm, naam: e.target.value })} className="rounded-xl" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Leeftijd vanaf</Label>
                  <Input type="number" value={groepForm.leeftijd_min} onChange={(e) => setGroepForm({ ...groepForm, leeftijd_min: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Leeftijd tot</Label>
                  <Input type="number" value={groepForm.leeftijd_max} onChange={(e) => setGroepForm({ ...groepForm, leeftijd_max: e.target.value })} className="rounded-xl" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Eindverantwoordelijke</Label>
                <Select value={groepForm.responsible_user_id || 'none'} onValueChange={(v) => setGroepForm({ ...groepForm, responsible_user_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies een persoon" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Geen</SelectItem>
                    {teamMembers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Kinderen in deze groep</Label>
                <div className="flex gap-2">
                  <Select onValueChange={(v) => { if (v && !groepForm.member_ids.includes(v)) setGroepForm({ ...groepForm, member_ids: [...groepForm.member_ids, v] }); }}>
                    <SelectTrigger className="rounded-xl flex-1"><SelectValue placeholder="Voeg een bestaand kind toe" /></SelectTrigger>
                    <SelectContent>
                      {members.filter((m) => !groepForm.member_ids.includes(m.id)).map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="sm" className="rounded-xl shrink-0" onClick={() => setNewChildFormOpen((open) => !open)}>
                    <Plus className="w-4 h-4 mr-1" />Nieuw kind
                  </Button>
                </div>
                {newChildFormOpen && (
                  <div className="space-y-2 bg-slate-50 rounded-xl p-3">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input placeholder="Naam van het kind" value={newChildName} onChange={(e) => setNewChildName(e.target.value)} className="rounded-xl flex-1" />
                      <Input type="date" placeholder="Geboortedatum" value={newChildBirthdate} onChange={(e) => setNewChildBirthdate(e.target.value)} className="rounded-xl sm:w-44" />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input placeholder="E-mail ouders (optioneel)" value={newChildEmail} onChange={(e) => setNewChildEmail(e.target.value)} className="rounded-xl flex-1" />
                      <Select value={newChildParentId || 'none'} onValueChange={(v) => setNewChildParentId(v === 'none' ? '' : v)}>
                        <SelectTrigger className="rounded-xl sm:w-56"><SelectValue placeholder="Ouder/voogd (optioneel)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Geen</SelectItem>
                          {members.filter((m) => m.household_role !== 'kind').map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="button" onClick={submitNewChild} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl w-full sm:w-auto">Toevoegen</Button>
                  </div>
                )}
                <p className="text-xs text-slate-400">Een nieuw kind wordt aangemaakt in het ledenbestand en direct aan deze groep gekoppeld.</p>
                {groepForm.member_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {groepForm.member_ids.map((id) => {
                      const member = members.find((m) => m.id === id);
                      return (
                        <span key={id} className="text-xs bg-slate-100 text-slate-700 rounded-full px-2.5 py-1 flex items-center gap-1.5">
                          {member ? member.name : id}
                          <button type="button" onClick={() => setGroepForm({ ...groepForm, member_ids: groepForm.member_ids.filter((m) => m !== id) })}><X className="w-3 h-3" /></button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Bijzonderheden (gevoelig, alleen zichtbaar voor eindverantwoordelijke en beheerders)</Label>
                <Textarea value={groepForm.gevoelige_notities} onChange={(e) => setGroepForm({ ...groepForm, gevoelige_notities: e.target.value })} className="rounded-xl resize-none" rows={3} />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setGroepDialogOpen(false)} className="rounded-xl">Annuleren</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">{editingGroep ? 'Opslaan' : 'Toevoegen'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Serie dialoog */}
        <Dialog open={serieDialogOpen} onOpenChange={setSerieDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>{editingSerie ? 'Serie bewerken' : 'Nieuwe lessenserie'}</DialogTitle></DialogHeader>
            <form onSubmit={submitSerie} className="space-y-4">
              <div className="space-y-2">
                <Label>Naam *</Label>
                <Input value={serieForm.naam} onChange={(e) => setSerieForm({ ...serieForm, naam: e.target.value })} className="rounded-xl" placeholder="Bijv. God zorgt voor jou" />
              </div>
              <div className="space-y-2">
                <Label>Leeftijdsgroep</Label>
                <Select value={serieForm.leeftijdsgroep_id || 'none'} onValueChange={(v) => setSerieForm({ ...serieForm, leeftijdsgroep_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies een groep" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Geen</SelectItem>
                    {groepen.map((g) => <SelectItem key={g.id} value={g.id}>{g.naam}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Hoofdthema</Label>
                  <Input value={serieForm.hoofdthema} onChange={(e) => setSerieForm({ ...serieForm, hoofdthema: e.target.value })} className="rounded-xl" placeholder="Vertrouwen op God" />
                </div>
                <div className="space-y-2">
                  <Label>Duur (weken)</Label>
                  <Input type="number" value={serieForm.duur_weken} onChange={(e) => setSerieForm({ ...serieForm, duur_weken: e.target.value })} className="rounded-xl" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notities</Label>
                <Textarea value={serieForm.notities} onChange={(e) => setSerieForm({ ...serieForm, notities: e.target.value })} className="rounded-xl resize-none" rows={2} />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setSerieDialogOpen(false)} className="rounded-xl">Annuleren</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">{editingSerie ? 'Opslaan' : 'Toevoegen'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Serie automatisch inplannen dialoog */}
        <Dialog open={Boolean(scheduleSerie)} onOpenChange={(open) => { if (!open) { setScheduleSerie(null); setScheduleResult(null); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Serie automatisch inplannen — {scheduleSerie?.naam}</DialogTitle></DialogHeader>
            <form onSubmit={submitSchedule} className="space-y-4">
              <p className="text-sm text-slate-500">
                Wijst opeenvolgende weekdatums toe aan de nog-ongedateerde lessen van deze serie (op volgorde). Lessen met al een datum worden nooit overschreven.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Weekdag</Label>
                  <Select value={String(scheduleForm.weekday)} onValueChange={(v) => setScheduleForm({ ...scheduleForm, weekday: Number(v) })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WEEKDAGEN.map((day) => <SelectItem key={day.value} value={String(day.value)}>{day.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Starttijd</Label><Input type="time" value={scheduleForm.start_time} onChange={(e) => setScheduleForm({ ...scheduleForm, start_time: e.target.value })} className="rounded-xl" /></div>
              </div>
              <div className="space-y-2"><Label>Startdatum</Label><Input type="date" value={scheduleForm.start_date} onChange={(e) => setScheduleForm({ ...scheduleForm, start_date: e.target.value })} className="rounded-xl" /></div>
              <div className="space-y-2">
                <Label className="text-xs">Data overslaan (optioneel, komma-gescheiden, YYYY-MM-DD)</Label>
                <Input value={scheduleForm.skip_dates} onChange={(e) => setScheduleForm({ ...scheduleForm, skip_dates: e.target.value })} placeholder="2026-08-16, 2026-08-23" className="rounded-xl" />
              </div>
              {scheduleResult && (
                <div className="space-y-2 rounded-xl bg-slate-50 p-3 text-sm">
                  <p><span className="font-medium text-teal-700">{scheduleResult.scheduled.length}</span> les(sen) ingepland: {scheduleResult.scheduled.map((s) => s.datum).join(', ') || '-'}</p>
                  <p><span className="font-medium text-slate-500">{scheduleResult.skipped_already_dated.length}</span> al gedateerd (overgeslagen)</p>
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setScheduleSerie(null)} className="rounded-xl">Sluiten</Button>
                <Button type="submit" disabled={scheduling} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">{scheduling ? 'Inplannen...' : 'Inplannen'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Kinderwerkdienst dialoog (los van lesinhoud) */}
        <Dialog open={dienstDialogOpen} onOpenChange={setDienstDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>{editingDienst ? 'Dienst bewerken' : 'Snel inplannen'}</DialogTitle></DialogHeader>
            <form onSubmit={submitDienst} className="space-y-4">
              <div className="space-y-2"><Label>Titel *</Label><Input value={dienstForm.title} onChange={(e) => setDienstForm({ ...dienstForm, title: e.target.value })} className="rounded-xl" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label>Datum *</Label><Input type="date" value={dienstForm.slot_date} onChange={(e) => setDienstForm({ ...dienstForm, slot_date: e.target.value })} className="rounded-xl" /></div>
                <div className="space-y-2"><Label>Starttijd *</Label><Input type="time" value={dienstForm.start_time} onChange={(e) => setDienstForm({ ...dienstForm, start_time: e.target.value })} className="rounded-xl" /></div>
                <div className="space-y-2"><Label>Eindtijd *</Label><Input type="time" value={dienstForm.end_time} onChange={(e) => setDienstForm({ ...dienstForm, end_time: e.target.value })} className="rounded-xl" /></div>
              </div>
              <div className="space-y-2"><Label>Rol (optioneel)</Label><Input value={dienstForm.role} onChange={(e) => setDienstForm({ ...dienstForm, role: e.target.value })} placeholder="Bijv. Vrijwilliger, Crècheleiding" className="rounded-xl" /></div>
              <div className="space-y-2">
                <Label>Groep (optioneel)</Label>
                <Select value={dienstForm.groep_id || 'none'} onValueChange={(v) => setDienstForm({ ...dienstForm, groep_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Geen groep" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Geen groep</SelectItem>
                    {groepen.map((g) => <SelectItem key={g.id} value={g.id}>{g.naam}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {editingDienst ? (
                <div className="space-y-2">
                  <Label>Kinderleider</Label>
                  <Select value={dienstForm.user_id || 'none'} onValueChange={(v) => setDienstForm({ ...dienstForm, user_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Nog niet gekozen" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nog niet gekozen</SelectItem>
                      {kinderwerkTeam.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const conflict = dienstSuggestions.find((c) => c.user_id === dienstForm.user_id);
                    return conflict?.conflicts?.length ? <p className="text-xs text-amber-700">⚠ {conflict.reasons.join(' · ')}</p> : null;
                  })()}
                  {!!dienstSuggestions.length && (
                    <div className="space-y-1 rounded-xl border border-dashed border-slate-300 p-2">
                      <p className="text-xs font-medium text-slate-500">Suggesties (kinderwerkteam, op beschikbaarheid)</p>
                      <div className="max-h-40 space-y-1 overflow-y-auto">
                        {dienstSuggestions.map((candidate) => (
                          <button
                            key={candidate.user_id}
                            type="button"
                            onClick={() => setDienstForm({ ...dienstForm, user_id: candidate.user_id })}
                            className={`flex w-full flex-col items-start gap-0.5 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-slate-50 ${dienstForm.user_id === candidate.user_id ? 'bg-teal-50' : ''}`}
                          >
                            <span className="font-medium text-slate-900">{candidate.name}</span>
                            {!!candidate.reasons.length && <span className="text-xs text-slate-500">{candidate.reasons.join(' · ')}</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {!dienstSuggestions.length && (
                    <p className="text-xs text-slate-400">Sla op en open opnieuw om suggesties en conflictwaarschuwingen te zien.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Kinderleiders (één of meerdere)</Label>
                  <Select onValueChange={(v) => { if (v && !dienstForm.user_ids.includes(v)) setDienstForm({ ...dienstForm, user_ids: [...dienstForm.user_ids, v] }); }}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Voeg een kinderleider toe" /></SelectTrigger>
                    <SelectContent>
                      {kinderwerkTeam.filter((m) => !dienstForm.user_ids.includes(m.id)).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {dienstForm.user_ids.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {dienstForm.user_ids.map((id) => (
                        <span key={id} className="text-xs bg-slate-100 text-slate-700 rounded-full px-2.5 py-1 flex items-center gap-1.5">
                          {kinderwerkTeam.find((m) => m.id === id)?.name || id}
                          <button type="button" onClick={() => setDienstForm({ ...dienstForm, user_ids: dienstForm.user_ids.filter((v) => v !== id) })}><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-400">
                    {dienstForm.user_ids.length > 1
                      ? `Maakt ${dienstForm.user_ids.length} losse diensten aan, één per gekozen kinderleider.`
                      : 'Laat leeg om een open dienst aan te maken die je later invult.'}
                  </p>
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setDienstDialogOpen(false)} className="rounded-xl">Annuleren</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">{editingDienst ? 'Opslaan' : 'Inplannen'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Lessen-per-serie dialoog */}
        <Dialog open={Boolean(lessenSerie)} onOpenChange={(open) => { if (!open) { setLessenSerie(null); setLessen([]); } }}>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Lessen — {lessenSerie?.naam}</DialogTitle></DialogHeader>
            {canManage && (
              <Button onClick={openCreateLes} variant="outline" className="rounded-xl w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />Nieuwe les
              </Button>
            )}
            <div className="divide-y divide-slate-100">
              {lessen.length === 0 ? (
                <p className="py-4 text-sm text-slate-500">Nog geen lessen in deze serie.</p>
              ) : (
                lessen.map((les) => (
                  <div key={les.id} className="flex items-center gap-3 py-3">
                    <span className="text-xs font-semibold text-slate-400 w-6 shrink-0">{les.volgorde}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{les.titel}</p>
                      <p className="text-xs text-slate-400">{les.datum || 'Geen datum'}{les.start_time ? ` ${les.start_time}` : ''}{les.duur_minuten ? ` · ${les.duur_minuten} min` : ''}</p>
                    </div>
                    {canManage && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openEditLes(les)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteLes(les)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Les-editor dialoog (volledig sjabloon) */}
        <Dialog open={lesDialogOpen} onOpenChange={setLesDialogOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingLes ? 'Les bewerken' : 'Nieuwe les'}</DialogTitle></DialogHeader>
            <form onSubmit={submitLes} className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2"><Calendar className="w-4 h-4" />Lesinformatie</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Titel *</Label><Input value={lesForm.titel} onChange={(e) => setLesForm({ ...lesForm, titel: e.target.value })} className="rounded-xl" /></div>
                  <div className="space-y-2"><Label>Volgorde in serie</Label><Input type="number" value={lesForm.volgorde} onChange={(e) => setLesForm({ ...lesForm, volgorde: e.target.value })} className="rounded-xl" /></div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>Datum</Label><Input type="date" value={lesForm.datum} onChange={(e) => setLesForm({ ...lesForm, datum: e.target.value })} className="rounded-xl" /></div>
                  <div className="space-y-2"><Label>Starttijd</Label><Input type="time" value={lesForm.start_time} onChange={(e) => setLesForm({ ...lesForm, start_time: e.target.value })} className="rounded-xl" /></div>
                  <div className="space-y-2"><Label>Duur (minuten)</Label><Input type="number" value={lesForm.duur_minuten} onChange={(e) => setLesForm({ ...lesForm, duur_minuten: e.target.value })} className="rounded-xl" /></div>
                </div>
                <div className="space-y-2">
                  <Label>Hoofdverantwoordelijke</Label>
                  <Select value={lesForm.hoofdverantwoordelijke_id || 'none'} onValueChange={(v) => setLesForm({ ...lesForm, hoofdverantwoordelijke_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies een persoon" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen</SelectItem>
                      {teamMembers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const conflict = lesSuggestions.find((c) => c.user_id === lesForm.hoofdverantwoordelijke_id);
                    return conflict?.conflicts?.length ? (
                      <p className="text-xs text-amber-700">⚠ {conflict.reasons.join(' · ')}</p>
                    ) : null;
                  })()}
                </div>
                <div className="space-y-2">
                  <Label>Vrijwilligers</Label>
                  <Select onValueChange={(v) => { if (v && !lesForm.vrijwilliger_ids.includes(v)) setLesForm({ ...lesForm, vrijwilliger_ids: [...lesForm.vrijwilliger_ids, v] }); }}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Voeg een vrijwilliger toe" /></SelectTrigger>
                    <SelectContent>
                      {teamMembers.filter((m) => !lesForm.vrijwilliger_ids.includes(m.id)).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {lesForm.vrijwilliger_ids.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {lesForm.vrijwilliger_ids.map((id) => {
                        const conflict = lesSuggestions.find((c) => c.user_id === id);
                        return (
                          <span key={id} className={`text-xs rounded-full px-2.5 py-1 flex items-center gap-1.5 ${conflict?.conflicts?.length ? 'bg-amber-50 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>
                            {teamMemberName(id)}
                            <button type="button" onClick={() => setLesForm({ ...lesForm, vrijwilliger_ids: lesForm.vrijwilliger_ids.filter((v) => v !== id) })}><X className="w-3 h-3" /></button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {editingLes && !!lesSuggestions.length && (
                    <div className="space-y-1 rounded-xl border border-dashed border-slate-300 p-2">
                      <p className="text-xs font-medium text-slate-500">Suggesties (kinderwerkteam, op beschikbaarheid)</p>
                      <div className="max-h-40 space-y-1 overflow-y-auto">
                        {lesSuggestions.filter((c) => !lesForm.vrijwilliger_ids.includes(c.user_id)).map((candidate) => (
                          <button
                            key={candidate.user_id}
                            type="button"
                            onClick={() => setLesForm({ ...lesForm, vrijwilliger_ids: [...lesForm.vrijwilliger_ids, candidate.user_id] })}
                            className="flex w-full flex-col items-start gap-0.5 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-slate-50"
                          >
                            <span className="font-medium text-slate-900">{candidate.name}</span>
                            {!!candidate.reasons.length && <span className="text-xs text-slate-500">{candidate.reasons.join(' · ')}</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {editingLes && !lesSuggestions.length && (!editingLes.datum || !editingLes.start_time || !editingLes.duur_minuten) && (
                    <p className="text-xs text-slate-400">Vul datum, starttijd en duur in en sla op om suggesties en conflictwaarschuwingen te zien.</p>
                  )}
                </div>
              </div>

              <div className="space-y-4 border-t border-slate-100 pt-4">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2"><BookOpen className="w-4 h-4" />Inhoud</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Lesdoel</Label><Textarea value={lesForm.lesdoel} onChange={(e) => setLesForm({ ...lesForm, lesdoel: e.target.value })} className="rounded-xl resize-none" rows={2} /></div>
                  <div className="space-y-2"><Label>Kernboodschap</Label><Textarea value={lesForm.kernboodschap} onChange={(e) => setLesForm({ ...lesForm, kernboodschap: e.target.value })} className="rounded-xl resize-none" rows={2} /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Bijbelgedeelte</Label><Input value={lesForm.bijbelgedeelte} onChange={(e) => setLesForm({ ...lesForm, bijbelgedeelte: e.target.value })} className="rounded-xl" placeholder="Bijv. Daniël 6" /></div>
                  <div className="space-y-2"><Label>Bijbeltekst om te onthouden</Label><Input value={lesForm.bijbeltekst} onChange={(e) => setLesForm({ ...lesForm, bijbeltekst: e.target.value })} className="rounded-xl" /></div>
                </div>
                <div className="space-y-2"><Label>Bijbelverhaal</Label><Textarea value={lesForm.bijbelverhaal} onChange={(e) => setLesForm({ ...lesForm, bijbelverhaal: e.target.value })} className="rounded-xl resize-none" rows={3} /></div>
                <div className="space-y-2"><Label>Welkomstactiviteit</Label><Textarea value={lesForm.welkomstactiviteit} onChange={(e) => setLesForm({ ...lesForm, welkomstactiviteit: e.target.value })} className="rounded-xl resize-none" rows={2} /></div>
                <div className="space-y-2"><Label>Aanbidding / liederen</Label><Textarea value={lesForm.aanbidding_liederen} onChange={(e) => setLesForm({ ...lesForm, aanbidding_liederen: e.target.value })} className="rounded-xl resize-none" rows={2} /></div>
                <div className="space-y-2"><Label>Gespreksvragen</Label><Textarea value={lesForm.gespreksvragen} onChange={(e) => setLesForm({ ...lesForm, gespreksvragen: e.target.value })} className="rounded-xl resize-none" rows={2} /></div>
                <div className="space-y-2"><Label>Spel of beweegactiviteit</Label><Textarea value={lesForm.spel_beweegactiviteit} onChange={(e) => setLesForm({ ...lesForm, spel_beweegactiviteit: e.target.value })} className="rounded-xl resize-none" rows={2} /></div>
                <div className="space-y-2">
                  <Label>Kinderwerkje (koppel een werkvorm uit de bibliotheek)</Label>
                  <Select value={lesForm.kinderwerkje_id || 'none'} onValueChange={(v) => setLesForm({ ...lesForm, kinderwerkje_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies een werkvorm" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen</SelectItem>
                      {werkvormen.map((w) => <SelectItem key={w.id} value={w.id}>{w.naam}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Gebed en afsluiting</Label><Textarea value={lesForm.gebed_afsluiting} onChange={(e) => setLesForm({ ...lesForm, gebed_afsluiting: e.target.value })} className="rounded-xl resize-none" rows={2} /></div>
                <div className="space-y-2"><Label>Thuisopdracht voor ouders en kinderen</Label><Textarea value={lesForm.thuisopdracht} onChange={(e) => setLesForm({ ...lesForm, thuisopdracht: e.target.value })} className="rounded-xl resize-none" rows={2} /></div>
              </div>

              <div className="space-y-4 border-t border-slate-100 pt-4">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2"><Boxes className="w-4 h-4" />Voorbereiding</h3>
                <div className="space-y-2">
                  <Label>Benodigde materialen</Label>
                  <MaterialenEditor items={lesForm.materialen} onChange={(items) => setLesForm({ ...lesForm, materialen: items })} inventoryItems={inventoryItems} />
                </div>
                <div className="space-y-2">
                  <Label>Printbestanden, video's en afbeeldingen (links)</Label>
                  <LinksEditor items={lesForm.materiaal_links} onChange={(items) => setLesForm({ ...lesForm, materiaal_links: items })} label="Link toevoegen" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Verwachte voorbereidingstijd (minuten)</Label><Input type="number" value={lesForm.voorbereidingstijd_minuten} onChange={(e) => setLesForm({ ...lesForm, voorbereidingstijd_minuten: e.target.value })} className="rounded-xl" /></div>
                </div>
                <div className="space-y-2"><Label>Opbouwinstructies</Label><Textarea value={lesForm.voorbereidingsinstructies} onChange={(e) => setLesForm({ ...lesForm, voorbereidingsinstructies: e.target.value })} className="rounded-xl resize-none" rows={2} /></div>
              </div>

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setLesDialogOpen(false)} className="rounded-xl">Annuleren</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">{editingLes ? 'Opslaan' : 'Toevoegen'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Werkvorm dialoog */}
        <Dialog open={werkvormDialogOpen} onOpenChange={setWerkvormDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingWerkvorm ? 'Werkvorm bewerken' : 'Nieuwe werkvorm'}</DialogTitle></DialogHeader>
            <form onSubmit={submitWerkvorm} className="space-y-4">
              <div className="space-y-2"><Label>Naam *</Label><Input value={werkvormForm.naam} onChange={(e) => setWerkvormForm({ ...werkvormForm, naam: e.target.value })} className="rounded-xl" placeholder="Bijv. Regenboog van Gods beloften" /></div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2"><Label>Leeftijd vanaf</Label><Input type="number" value={werkvormForm.leeftijd_min} onChange={(e) => setWerkvormForm({ ...werkvormForm, leeftijd_min: e.target.value })} className="rounded-xl" /></div>
                <div className="space-y-2"><Label>Leeftijd tot</Label><Input type="number" value={werkvormForm.leeftijd_max} onChange={(e) => setWerkvormForm({ ...werkvormForm, leeftijd_max: e.target.value })} className="rounded-xl" /></div>
                <div className="space-y-2"><Label>Duur (minuten)</Label><Input type="number" value={werkvormForm.duur_minuten} onChange={(e) => setWerkvormForm({ ...werkvormForm, duur_minuten: e.target.value })} className="rounded-xl" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Thema</Label><Input value={werkvormForm.thema} onChange={(e) => setWerkvormForm({ ...werkvormForm, thema: e.target.value })} className="rounded-xl" /></div>
                <div className="space-y-2"><Label>Bijbelverhaal</Label><Input value={werkvormForm.bijbelverhaal} onChange={(e) => setWerkvormForm({ ...werkvormForm, bijbelverhaal: e.target.value })} className="rounded-xl" /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={werkvormForm.type || 'none'} onValueChange={(v) => setWerkvormForm({ ...werkvormForm, type: v === 'none' ? '' : v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies type" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Geen</SelectItem>{WERKVORM_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Binnen/buiten</Label>
                  <Select value={werkvormForm.binnen_buiten || 'none'} onValueChange={(v) => setWerkvormForm({ ...werkvormForm, binnen_buiten: v === 'none' ? '' : v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Geen</SelectItem>{BINNEN_BUITEN_OPTIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Moeilijkheid</Label>
                  <Select value={werkvormForm.moeilijkheid || 'none'} onValueChange={(v) => setWerkvormForm({ ...werkvormForm, moeilijkheid: v === 'none' ? '' : v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Geen</SelectItem>{MOEILIJKHEID_OPTIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Groepsgrootte</Label><Input value={werkvormForm.groepsgrootte} onChange={(e) => setWerkvormForm({ ...werkvormForm, groepsgrootte: e.target.value })} className="rounded-xl" placeholder="Bijv. 4-8 kinderen" /></div>
              <div className="space-y-2">
                <Label>Materialen</Label>
                <MaterialenEditor items={werkvormForm.materialen} onChange={(items) => setWerkvormForm({ ...werkvormForm, materialen: items })} inventoryItems={inventoryItems} />
              </div>
              <div className="space-y-2"><Label>Voorbereiding</Label><Textarea value={werkvormForm.voorbereiding} onChange={(e) => setWerkvormForm({ ...werkvormForm, voorbereiding: e.target.value })} className="rounded-xl resize-none" rows={2} /></div>
              <div className="space-y-2"><Label>Uitvoering (stappenplan)</Label><Textarea value={werkvormForm.uitvoering} onChange={(e) => setWerkvormForm({ ...werkvormForm, uitvoering: e.target.value })} className="rounded-xl resize-none" rows={3} /></div>
              <div className="space-y-2">
                <Label>Bijlagen (printblad, uitleg voor ouders)</Label>
                <LinksEditor items={werkvormForm.bijlage_links} onChange={(items) => setWerkvormForm({ ...werkvormForm, bijlage_links: items })} label="Bijlage toevoegen" />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setWerkvormDialogOpen(false)} className="rounded-xl">Annuleren</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">{editingWerkvorm ? 'Opslaan' : 'Toevoegen'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Notities dialoog */}
        <Dialog open={Boolean(notitiesWerkvorm)} onOpenChange={(open) => { if (!open) { setNotitiesWerkvorm(null); setNotities([]); } }}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Notities — {notitiesWerkvorm?.naam}</DialogTitle></DialogHeader>
            <form onSubmit={submitNotitie} className="space-y-3 border-b border-slate-100 pb-4">
              <Textarea value={notitieText} onChange={(e) => setNotitieText(e.target.value)} placeholder="Wat kan een volgende keer beter?" className="rounded-xl resize-none" rows={2} />
              <div className="flex items-center gap-3">
                <Select value={notitieRating || 'none'} onValueChange={(v) => setNotitieRating(v === 'none' ? '' : v)}>
                  <SelectTrigger className="rounded-xl w-40"><SelectValue placeholder="Beoordeling" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Geen beoordeling</SelectItem>
                    {[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n} ster{n > 1 ? 'ren' : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl ml-auto">Notitie toevoegen</Button>
              </div>
            </form>
            <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {notities.length === 0 ? (
                <p className="py-4 text-sm text-slate-500">Nog geen notities.</p>
              ) : (
                notities.map((n) => (
                  <div key={n.id} className="py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{n.user_name}</span>
                      {n.beoordeling && (
                        <span className="text-xs text-amber-500 flex items-center gap-0.5">
                          {Array.from({ length: n.beoordeling }).map((_, i) => <Star key={i} className="w-3 h-3 fill-amber-400" />)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5">{n.notitie}</p>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Kinderwerkteam: teamlid toevoegen */}
        <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Teamlid toevoegen</DialogTitle></DialogHeader>
            <div className="flex gap-1 rounded-md bg-slate-100 p-1">
              <button type="button" onClick={() => { setTeamDialogSource('team'); setTeamInviteLink(null); }} className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium ${teamDialogSource === 'team' ? 'bg-white shadow-sm' : 'text-slate-500'}`}>Bestaand teamlid</button>
              <button type="button" onClick={() => { setTeamDialogSource('member'); setTeamInviteLink(null); }} className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium ${teamDialogSource === 'member' ? 'bg-white shadow-sm' : 'text-slate-500'}`}>Gemeentelid</button>
            </div>

            {teamDialogSource === 'team' && (
              <form onSubmit={addExistingTeamMember} className="space-y-3">
                <p className="text-sm text-slate-500">Kies iemand die al een Zoea-account heeft (via Team algemeen).</p>
                <Select value={selectedTeamUserId || 'none'} onValueChange={(v) => setSelectedTeamUserId(v === 'none' ? '' : v)}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies teamlid" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Kies teamlid</SelectItem>
                    {availableTeamUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({(u.roles || []).join(', ')})</SelectItem>)}
                  </SelectContent>
                </Select>
                {!availableTeamUsers.length && <p className="text-xs text-slate-400">Iedereen uit Team algemeen staat al in het kinderwerkteam.</p>}
                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" onClick={() => setTeamDialogOpen(false)} className="rounded-xl">Sluiten</Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">Toevoegen</Button>
                </DialogFooter>
              </form>
            )}

            {teamDialogSource === 'member' && (
              teamInviteLink ? (
                <div className="space-y-3">
                  <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800">Uitnodiging verstuurd. Deel eventueel ook deze link:</div>
                  <Input readOnly value={teamInviteLink} onFocus={(e) => e.target.select()} className="rounded-xl" />
                  <DialogFooter><Button type="button" onClick={() => setTeamDialogOpen(false)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">Sluiten</Button></DialogFooter>
                </div>
              ) : (
                <form onSubmit={inviteTeamMember} className="space-y-3">
                  <p className="text-sm text-slate-500">Kies een gemeentelid uit Ledenbestand. Ze krijgen een e-mail om zelf een wachtwoord in te stellen, en loggen daarna alleen in bij Kinderwerk.</p>
                  <Select value={selectedTeamMemberId || 'none'} onValueChange={(v) => setSelectedTeamMemberId(v === 'none' ? '' : v)}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies gemeentelid" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kies gemeentelid</SelectItem>
                      {availableTeamMembers.map((m) => <SelectItem key={m.id} value={m.id} disabled={!m.email}>{m.name}{!m.email ? ' (geen e-mailadres)' : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={teamInviteRole} onValueChange={setTeamInviteRole}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kinderwerkvrijwilliger">Kinderwerkvrijwilliger</SelectItem>
                      <SelectItem value="kinderwerkleider">Kinderwerkleider</SelectItem>
                      <SelectItem value="coordinator_kinderwerk">Kinderwerkcoördinator</SelectItem>
                    </SelectContent>
                  </Select>
                  {!availableTeamMembers.length && <p className="text-xs text-slate-400">Geen (nieuwe) gemeenteleden gevonden in Ledenbestand.</p>}
                  <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => setTeamDialogOpen(false)} className="rounded-xl">Sluiten</Button>
                    <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">Uitnodigen</Button>
                  </DialogFooter>
                </form>
              )
            )}
          </DialogContent>
        </Dialog>
      </div>
    </KinderwerkLayout>
  );
};

export default KinderwerkPage;
